import { Injectable } from "@angular/core";
import { EsimService } from "./features/esim/esim.service";
import { GlobalDataService } from "./services/global-data.service";
import { AWSLambda } from "./AWSLambda.service";

/**
 * Servicio encargado de inicializar la aplicación cargando datos globales
 * y cacheando resultados relevantes para optimizar la experiencia del usuario.
 *
 * Realiza la carga y validación en caché para:
 * - Paquetes eSIM filtrados
 * - Productos SendMoney (servicio no disponible, comentado para evitar errores en consola)
 * - URLs para transfers y créditos
 */
@Injectable({
  providedIn: 'root'
})
export class AppInitService {
  private CACHE_KEY_ESIM = 'filteredCountriesCache';
  // private CACHE_KEY_SENDMONEY = 'sendMoneyCache';
  private CACHE_TRANSFERSURL = 'transfersUrlCache';
  private CACHE_CREDITURL = 'creditsUrlCache';

  constructor(
    private globalDataService: GlobalDataService,
    private esimService: EsimService,
    private lambda: AWSLambda
  ) { }

  /**
   * Método principal para inicializar la app.
   *
   * Lee primero los datos cacheados en localStorage y si están vigentes
   * los utiliza para asignarlos al GlobalDataService.
   *
   * Si los datos cacheados no existen o han expirado, realiza las llamadas
   * a servicios para obtener la información y actualiza el cache.
   */
  async init(): Promise<void> {
    const now = Date.now();
    const expiration = this.globalDataService.CACHE_EXPIRATION_MS;

    const esimCache = this.parseCache(localStorage.getItem(this.CACHE_KEY_ESIM));
    // const sendMoneyCache = this.parseCache(localStorage.getItem(this.CACHE_KEY_SENDMONEY));
    const transfersCache = this.parseCache(localStorage.getItem(this.CACHE_TRANSFERSURL));
    const creditsCache = this.parseCache(localStorage.getItem(this.CACHE_CREDITURL));

    if (esimCache && now - esimCache.timestamp < expiration) {
      this.globalDataService.esimFilteredCountries = esimCache.data;
    } else {
      this.refreshEsimCatalog();
    }

    // if (sendMoneyCache && now - sendMoneyCache.timestamp < expiration) {
    //   this.globalDataService.sendMoneyData = sendMoneyCache.data;
    // } else {
    //   this.refreshSendMoney();
    // }

    if (transfersCache && now - transfersCache.timestamp < expiration) {
      this.globalDataService.transfersUrlData = transfersCache.data;
    }
    if (creditsCache && now - creditsCache.timestamp < expiration) {
      this.globalDataService.creditsUrlData = creditsCache.data;
    }
    if (!transfersCache || !creditsCache ||
        now - (transfersCache?.timestamp ?? 0) >= expiration ||
        now - (creditsCache?.timestamp ?? 0) >= expiration) {
      this.refreshUrls();
    }
  }

  /**
   * Refresca en background los datos de localStorage que hayan expirado.
   * Llamado desde AppComponent en cada navegación y al recuperar el foco de la pestaña.
   */
  refreshIfStale(): void {
    const now = Date.now();
    const expiration = this.globalDataService.CACHE_EXPIRATION_MS;

    const esimCache = this.parseCache(localStorage.getItem(this.CACHE_KEY_ESIM));
    if (!esimCache || now - esimCache.timestamp >= expiration) {
      this.refreshEsimCatalog();
    }

    // const sendMoneyCache = this.parseCache(localStorage.getItem(this.CACHE_KEY_SENDMONEY));
    // if (!sendMoneyCache || now - sendMoneyCache.timestamp >= expiration) {
    //   this.refreshSendMoney();
    // }

    const transfersCache = this.parseCache(localStorage.getItem(this.CACHE_TRANSFERSURL));
    const creditsCache = this.parseCache(localStorage.getItem(this.CACHE_CREDITURL));
    if (!transfersCache || !creditsCache ||
        now - transfersCache.timestamp >= expiration ||
        now - creditsCache.timestamp >= expiration) {
      this.refreshUrls();
    }
  }

  private parseCache(raw: string | null): { data: any; timestamp: number } | null {
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  }

  private refreshEsimCatalog(): void {
    (async () => {
      try {
        const tipos = ['Local', 'Regional', 'Global'];
        const results = await Promise.all(
          tipos.map(tipo =>
            this.esimService.getfilteredPackages(tipo).then(data => ({ tipo, data }))
          )
        );
        const filteredCountries: Record<string, any> = {};
        results.forEach(({ tipo, data }) => { filteredCountries[tipo] = data; });
        this.globalDataService.esimFilteredCountries = filteredCountries;
        localStorage.setItem(this.CACHE_KEY_ESIM, JSON.stringify({ data: filteredCountries, timestamp: Date.now() }));
      } catch (e) {
        console.error('[AppInit] Error al cargar catálogo eSIM en background', e);
      }
    })();
  }

  // private refreshSendMoney(): void {
  //   (async () => {
  //     try {
  //       const sendMoneyData = await this.lambda.getLambdaFunction('lambda', '/revolu/api', {
  //         queryStringParameters: { method: 'products' }
  //       });
  //       this.globalDataService.sendMoneyData = sendMoneyData;
  //       localStorage.setItem(this.CACHE_KEY_SENDMONEY, JSON.stringify({ data: sendMoneyData, timestamp: Date.now() }));
  //     } catch (e) {
  //       console.error('[AppInit] Error al cargar SendMoney en background', e);
  //     }
  //   })();
  // }

  private refreshUrls(): void {
    (async () => {
      try {
        const response = await this.lambda.getLambdaFunction('lambda', '/get/datos', {
          queryStringParameters: { filtro: 'urls', opcion: 2, owner: 'initfront' }
        });
        const transfersUrlData = response.datos?.urls?.transfers_url?.valor || '';
        const creditsUrlData = response.datos?.urls?.credits_url?.valor || '';
        this.globalDataService.transfersUrlData = transfersUrlData;
        this.globalDataService.creditsUrlData = creditsUrlData;
        const now = Date.now();
        localStorage.setItem(this.CACHE_TRANSFERSURL, JSON.stringify({ data: transfersUrlData, timestamp: now }));
        localStorage.setItem(this.CACHE_CREDITURL, JSON.stringify({ data: creditsUrlData, timestamp: now }));
      } catch (e) {
        console.error('[AppInit] Error al cargar URLs en background', e);
      }
    })();
  }
}
